import { useState, useEffect } from 'react';
import { getUnidades, getEmpresaAtual } from '../pages/api';

// Cache simples em módulo — evita repetir a chamada de API em cada
// componente que usa o hook (a lista de unidades muda raramente).
let unidadesCache = null;
let empresaIdCache = null;

/**
 * Hook substituto de `config.unidades`.
 * Uso: const { unidades, loadingUnidades } = useUnidades();
 *
 * Retorna sempre as unidades ATIVAS (mesmo comportamento que o dropdown
 * de Configurações usa por padrão — unidades inativadas não aparecem
 * aqui, então excluir/inativar uma unidade agora reflete em todo o
 * sistema, resolvendo o problema do config.json desatualizado).
 *
 * Formato de cada item: { _id, nome, empresa, ativo }
 * IMPORTANTE: não tem mais campo `.id` numérico — qualquer comparação
 * que antes usava `option.id === value.id` precisa virar
 * `option._id === value._id`. Para os Autocompletes de Unidade, use o
 * helper `isSameUnidade` exportado abaixo em vez de escrever a
 * comparação inline (ver comentário na própria função).
 */
export function useUnidades() {
  const [unidades, setUnidades] = useState(unidadesCache || []);
  const [loadingUnidades, setLoadingUnidades] = useState(!unidadesCache);

  useEffect(() => {
    let cancelado = false;

    if (unidadesCache) {
      setUnidades(unidadesCache);
      setLoadingUnidades(false);
      return () => {
        cancelado = true;
      };
    }

    const fetchUnidades = async () => {
      setLoadingUnidades(true);
      try {
        if (!empresaIdCache) {
          const empresa = await getEmpresaAtual();
          empresaIdCache = empresa?._id || null;
        }
        const resultado = await getUnidades(empresaIdCache);
        if (!cancelado) {
          unidadesCache = resultado || [];
          setUnidades(unidadesCache);
        }
      } catch (error) {
        console.error('Erro ao buscar unidades:', error);
        if (!cancelado) setUnidades([]);
      } finally {
        if (!cancelado) setLoadingUnidades(false);
      }
    };

    fetchUnidades();

    return () => {
      cancelado = true;
    };
  }, []);

  return { unidades, loadingUnidades };
}

/**
 * Comparador de Unidade para o `isOptionEqualToValue` dos Autocompletes.
 * Uso: isOptionEqualToValue={isSameUnidade}
 *
 * Por que não usar `(option, value) => option._id === value._id` direto:
 *
 * 1. Registros LEGADOS foram salvos no esquema antigo `{ id: número, nome }`,
 *    sem `_id`. Nesses casos os dois lados são `undefined` e a comparação
 *    estrita retorna `true` — o MUI casa com a PRIMEIRA opção da lista e
 *    exibe a unidade errada silenciosamente. Por isso a checagem exige que
 *    AMBOS os `_id` existam antes de compará-los, caindo para o nome quando
 *    não existem.
 * 2. O MUI chama o comparador com valor vazio em alguns caminhos (os campos
 *    usam `value={formData.unidade || null}`), daí a guarda de null.
 * 3. `_id` pode chegar como ObjectId ou como string dependendo de como o
 *    documento foi serializado; `String()` nos dois lados evita o falso
 *    negativo.
 *
 * Ressalva conhecida: o fallback por nome só casa se o nome do registro
 * antigo ainda existir no cadastro novo. Unidade renomeada ou inativada
 * deixa o campo vazio no registro legado — comportamento correto, mas
 * esperado.
 *
 * QUANDO A MIGRAÇÃO DE DADOS FOR FEITA (todo documento com `unidade._id`),
 * o fallback por nome pode ser removido e esta função vira apenas a
 * comparação de `_id` — mexendo só aqui, sem caçar cópias inline.
 */
export function isSameUnidade(option, value) {
  if (!option || !value) return false;
  if (option._id && value._id) return String(option._id) === String(value._id);
  return option.nome === value.nome;
}

// Função para invalidar o cache manualmente (ex.: depois de criar/editar
// uma unidade na tela de Configurações, se ela estiver montada ao mesmo
// tempo que outras telas que já carregaram o cache).
export function invalidateUnidadesCache() {
  unidadesCache = null;
  empresaIdCache = null;
}

export default useUnidades;